import React, { useMemo, useState } from "react";
import Table from "../../../components/Table";
import TitleButtons from "../../../components/TitleButtons";
import {
  useVendasSuprimentos,
  RetornoStatusUI,
  SaidaRowMeta,
  SaidaRowUI,
} from "./vendasData";
import { generatePdfReport } from "../../../utils/pdf/generatePdfReport";

type ItemPVModal = {
  originalIndex: number;
  row: SaidaRowUI;
  meta: SaidaRowMeta;
};

type ModalState = {
  open: boolean;
  pv: string;
  itens: ItemPVModal[];
};

type SelecoesState = Record<
  number,
  {
    marcado: boolean;
    quantidade: string;
  }
>;

const URL_SOLICITAR =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/retornos/solicitar";

const URL_CANCELAR =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/retornos/cancelar";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#0c2b53",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 12,
  marginBottom: 20,
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#0c2b53",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const formRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginBottom: 14,
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  minHeight: 100,
  resize: "vertical",
  fontFamily: "inherit",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#FFFFFF",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonMutedStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#f8fafc",
  color: "#0c2b53",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const buttonDangerStyle: React.CSSProperties = {
  border: "1px solid #7D1A1A",
  background: "#ffffff",
  color: "#7D1A1A",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const badgeBaseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
};

function getStatusLabel(status: RetornoStatusUI) {
  switch (status) {
    case "solicitado":
      return "p/ retorno";
    case "aprovado":
      return "Aprovado";
    case "negado":
      return "Negado";
    default:
      return "Sem solicitação";
  }
}

function getStatusBadgeStyle(status: RetornoStatusUI): React.CSSProperties {
  switch (status) {
    case "solicitado":
      return {
        ...badgeBaseStyle,
        background: "#FFD6CD",
        color: "#92400e",
        border: "1px solid #060606",
      };
    case "aprovado":
      return {
        ...badgeBaseStyle,
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      };
    case "negado":
      return {
        ...badgeBaseStyle,
        background: "#f8d7da",
        color: "#991b1b",
        border: "1px solid #f5a3ad",
      };
    default:
      return {
        ...badgeBaseStyle,
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      };
  }
}

function canRequestStatus(status: RetornoStatusUI) {
  return status === "none" || status === "negado";
}

function canCancelStatus(status: RetornoStatusUI) {
  return status === "solicitado";
}

const SuprimentosVendas: React.FC = () => {
  const headers = [
    "PV",
    "Tipo",
    "Código",
    "Nome do Produto",
    "Quantidade",
    "Custo Unitário",
    "Cliente",
    "Responsável",
    "Data",
    "Hora",
  ];

  const { rows, rowMeta, loading, refresh } = useVendasSuprimentos();

  const columnWidths = [
    "60px", // PV
    "80px", // Tipo
    "70px", // Código
    "300px", // Nome do Produto
    "60px", // Quantidade
    "100px", // Custo Unitário
    "160px", // Cliente
    "160px", // Responsável
    "90px", // Data
    "60px", // Hora
  ];

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );

  const [modal, setModal] = useState<ModalState>({
    open: false,
    pv: "",
    itens: [],
  });

  const [selecao, setSelecao] = useState<SelecoesState>({});
  const [motivo, setMotivo] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [cancelandoIds, setCancelandoIds] = useState<number[]>([]);

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  const footerLines = useMemo(() => {
    let totalQuantidade = 0;

    rows.forEach((row) => {
      const qtd = Number(String(row[4] ?? "").replace(",", "."));
      if (isFinite(qtd)) totalQuantidade += qtd;
    });

    return [`Total de Itens Vendidos: ${totalQuantidade}`];
  }, [rows]);

  const infoRows = useMemo(() => rows, [rows]);

  const openModalPorPV = (pv: string) => {
    const itensDaPV: ItemPVModal[] = rows
      .map((row, index) => {
        const meta = rowMeta[index];
        if (!meta) return null;
        if ((row[0] ?? "") !== pv) return null;

        return {
          originalIndex: index,
          row,
          meta,
        };
      })
      .filter(Boolean) as ItemPVModal[];

    const initialSelecao: SelecoesState = {};
    for (const item of itensDaPV) {
      const permitido = canRequestStatus(item.meta.retornoStatus);
      initialSelecao[item.originalIndex] = {
        marcado: permitido,
        quantidade: String(item.meta.quantidade ?? ""),
      };
    }

    setModal({
      open: true,
      pv,
      itens: itensDaPV,
    });
    setSelecao(initialSelecao);
    setMotivo("");
  };

  const closeModal = () => {
    if (submitting || cancelandoIds.length > 0) return;

    setModal({
      open: false,
      pv: "",
      itens: [],
    });
    setSelecao({});
    setMotivo("");
  };

  const itensSolicitaveis = useMemo(() => {
    return modal.itens.filter((item) =>
      canRequestStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const itensSelecionados = useMemo(() => {
    return itensSolicitaveis.filter(
      (item) => selecao[item.originalIndex]?.marcado
    );
  }, [itensSolicitaveis, selecao]);

  const itensComSolicitacaoPendente = useMemo(() => {
    return modal.itens.filter((item) =>
      canCancelStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const marcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };
      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: true,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(item.meta.quantidade ?? ""),
        };
      }
      return next;
    });
  };

  const desmarcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };
      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: false,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(item.meta.quantidade ?? ""),
        };
      }
      return next;
    });
  };

  const toggleItem = (originalIndex: number, checked: boolean) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        marcado: checked,
      },
    }));
  };

  const setQuantidadeItem = (originalIndex: number, value: string) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        quantidade: value,
      },
    }));
  };

  async function solicitarRetornoPV() {
    if (!itensSelecionados.length) {
      alert("Selecione ao menos um item para solicitar retorno.");
      return;
    }

    for (const item of itensSelecionados) {
      const quantidade = Number(selecao[item.originalIndex]?.quantidade);

      if (!Number.isFinite(quantidade) || quantidade <= 0) {
        alert(`Informe uma quantidade válida para o item ${item.row[3]}.`);
        return;
      }

      if (quantidade > item.meta.quantidade) {
        alert(
          `A quantidade do item ${item.row[3]} não pode ser maior que ${item.meta.quantidade}.`
        );
        return;
      }
    }

    setSubmitting(true);

    try {
      for (const item of itensSelecionados) {
        const quantidade = Number(selecao[item.originalIndex]?.quantidade);

        const res = await fetch(URL_SOLICITAR, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            saida_movimentacao_id: item.meta.movimentacaoId,
            produto_id: item.meta.produtoId,
            quantidade,
            solicitante_responsavel_id: 1,
            solicitante_nome: "Usuário Logado",
            motivo: motivo.trim() || null,
          }),
        });

        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(
            json?.erro ||
              json?.erros?.join("\n") ||
              `Falha ao solicitar retorno do item ${item.row[3]}`
          );
        }
      }

      await refresh();
      closeModal();
    } catch (e: any) {
      alert(e?.message || "Falha ao solicitar retorno.");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelarSolicitacao(item: ItemPVModal) {
    const solicitacaoId = item.meta.retornoSolicitacaoId;

    if (!solicitacaoId) {
      alert("Solicitação não encontrada para este item.");
      return;
    }

    const confirmar = window.confirm(
      `Cancelar a solicitação de retorno do item "${item.row[3]}"?`
    );
    if (!confirmar) return;

    setCancelandoIds((prev) => [...prev, item.originalIndex]);

    try {
      const res = await fetch(URL_CANCELAR, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          solicitacao_id: solicitacaoId,
          cancelado_por_id: 1,
          cancelado_por_nome: "Usuário Logado",
        }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok || !json?.ok) {
        throw new Error(
          json?.erro ||
            json?.erros?.join("\n") ||
            `Falha ao cancelar solicitação do item ${item.row[3]}`
        );
      }

      await refresh();

      setModal((prev) => ({
        ...prev,
        itens: prev.itens.map((x) =>
          x.originalIndex === item.originalIndex
            ? {
                ...x,
                meta: {
                  ...x.meta,
                  retornoStatus: "none",
                  retornoSolicitacaoId: null,
                  retornoQuantidade: null,
                  retornoMotivo: null,
                  retornoObservacao: null,
                  retornoMotivoNegativa: null,
                  retornoCriadoEm: null,
                  retornoAnalisadoEm: null,
                },
              }
            : x
        ),
      }));
    } catch (e: any) {
      alert(e?.message || "Falha ao cancelar solicitação.");
    } finally {
      setCancelandoIds((prev) =>
        prev.filter((id) => id !== item.originalIndex)
      );
    }
  }

  return (
    <div>
      <TitleButtons
        title="Suprimentos - Vendas"
        buttons={[
          {
            label: "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Vendas",
                fileNamePrefix: "Comercial-Vendas-finalizadas",
                subtitle:
                  "Informações Saídas Finalizadas de Material para Venda",
                headers,
                rows,
                custoColIndex: 5,
                enabledColumns: enabledColumnIndexes,
                footerLines,
              }),
            variant: "secondary",
            dropdownOptions: headers.map((h, i) => ({
              label: h,
              checked: selectedColumns[i],
            })),
            onOptionChange: (index, checked) => {
              const updated = [...selectedColumns];
              updated[index] = checked;
              setSelectedColumns(updated);
            },
          },
        ]}
      />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando vendas…</div>
      ) : (
        <Table
          headers={headers}
          rows={infoRows}
          columnWidths={columnWidths}
          isTableStandby
          osColumnIndex={0}
          onDoubleClick={(viewIndex, rowData, originalIndex) => {
            const idx = originalIndex ?? viewIndex;
            const row = rows[idx];
            const pv = String(row?.[0] ?? rowData?.[0] ?? "");
            if (!pv) return;
            openModalPorPV(pv);
          }}
          getRowStyle={(_row, _viewIndex, originalIndex) => {
            const meta = rowMeta[originalIndex];
            if (!meta) return undefined;

            if (meta.retornoStatus === "solicitado") {
              return { background: "#EE807C83" };
            }

            if (meta.retornoStatus === "negado") {
              return { background: "#f8d7da" };
            }

            return undefined;
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>
                  Solicitação de retorno ao standby
                </h3>
                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  P.V. <strong>{modal.pv}</strong>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <h4 style={sectionTitleStyle}>Resumo da P.V.</h4>

              <div style={gridStyle}>
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>P.V.</span>
                  <div style={fieldValueStyle}>{modal.pv}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens da P.V.</span>
                  <div style={fieldValueStyle}>{modal.itens.length}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens selecionados</span>
                  <div style={fieldValueStyle}>{itensSelecionados.length}</div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 16,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={marcarTodos}
                  style={buttonMutedStyle}
                  disabled={
                    !itensSolicitaveis.length ||
                    submitting ||
                    cancelandoIds.length > 0
                  }
                >
                  Marcar todos
                </button>

                <button
                  type="button"
                  onClick={desmarcarTodos}
                  style={buttonMutedStyle}
                  disabled={
                    !itensSolicitaveis.length ||
                    submitting ||
                    cancelandoIds.length > 0
                  }
                >
                  Desmarcar todos
                </button>
              </div>

              <h4 style={sectionTitleStyle}>Itens da P.V.</h4>

              <div
                style={{
                  border: "1px solid #d1d5db",
                  marginBottom: 20,
                  overflow: "hidden",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 14,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#0c2b53", color: "#ffffff" }}>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 60,
                        }}
                      >
                        Sel.
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          textAlign: "left",
                        }}
                      >
                        Código
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          textAlign: "left",
                        }}
                      >
                        Produto
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 110,
                        }}
                      >
                        Qtd. vendida
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 120,
                        }}
                      >
                        Qtd. retorno
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 130,
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 140,
                        }}
                      >
                        Custo Unitário
                      </th>
                      <th
                        style={{
                          padding: 10,
                          border: "1px solid #d1d5db",
                          width: 170,
                        }}
                      >
                        Ação
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {modal.itens.map((item, idx) => {
                      const allowed = canRequestStatus(item.meta.retornoStatus);
                      const canCancel = canCancelStatus(
                        item.meta.retornoStatus
                      );
                      const selected = !!selecao[item.originalIndex]?.marcado;
                      const quantidade =
                        selecao[item.originalIndex]?.quantidade ?? "";
                      const cancelando = cancelandoIds.includes(
                        item.originalIndex
                      );

                      return (
                        <tr
                          key={item.originalIndex}
                          style={{
                            background: idx % 2 === 0 ? "#fbfcfe" : "#f6f8fb",
                          }}
                        >
                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={!allowed || submitting || cancelando}
                              onChange={(e) =>
                                toggleItem(item.originalIndex, e.target.checked)
                              }
                            />
                          </td>

                          <td
                            style={{ padding: 10, border: "1px solid #d1d5db" }}
                          >
                            {item.row[2]}
                          </td>

                          <td
                            style={{ padding: 10, border: "1px solid #d1d5db" }}
                          >
                            <div>{item.row[3]}</div>

                            {item.meta.retornoMotivo && !allowed && (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: "#6b7280",
                                }}
                              >
                                <strong>Motivo anterior:</strong>{" "}
                                {item.meta.retornoMotivo}
                              </div>
                            )}

                            {item.meta.retornoMotivoNegativa && (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "#991b1b",
                                }}
                              >
                                <strong>Negativa:</strong>{" "}
                                {item.meta.retornoMotivoNegativa}
                              </div>
                            )}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {item.meta.quantidade}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {allowed ? (
                              <input
                                type="number"
                                min={1}
                                max={item.meta.quantidade}
                                value={quantidade}
                                disabled={!selected || submitting || cancelando}
                                onChange={(e) =>
                                  setQuantidadeItem(
                                    item.originalIndex,
                                    e.target.value
                                  )
                                }
                                style={{
                                  width: 90,
                                  border: "1px solid #0c2b53",
                                  padding: "6px 8px",
                                  textAlign: "center",
                                }}
                              />
                            ) : (
                              <span>{item.meta.retornoQuantidade ?? "-"}</span>
                            )}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            <span
                              style={getStatusBadgeStyle(
                                item.meta.retornoStatus
                              )}
                            >
                              {getStatusLabel(item.meta.retornoStatus)}
                            </span>
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {item.row[5]}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {canCancel ? (
                              <button
                                type="button"
                                onClick={() => cancelarSolicitacao(item)}
                                style={buttonDangerStyle}
                                disabled={submitting || cancelando}
                              >
                                {cancelando
                                  ? "Cancelando..."
                                  : "Cancelar solicitação"}
                              </button>
                            ) : (
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {!!itensSolicitaveis.length && (
                <div style={formRowStyle}>
                  <label style={fieldLabelStyle}>Motivo</label>
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    style={textareaStyle}
                    placeholder="Descreva o motivo do retorno ao standby"
                    disabled={submitting || cancelandoIds.length > 0}
                  />
                </div>
              )}

              {!itensSolicitaveis.length &&
                !itensComSolicitacaoPendente.length && (
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: 14,
                      background: "#f9fafb",
                      color: "#374151",
                    }}
                  >
                    Todos os itens desta P.V. já foram aprovados ou não possuem
                    ação disponível.
                  </div>
                )}
            </div>

            <div style={modalFooterStyle}>
              <div style={{ fontSize: 13, color: "#374151" }}>
                {itensSelecionados.length
                  ? `${itensSelecionados.length} item(ns) selecionado(s)`
                  : "Nenhum item selecionado"}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={buttonSecondaryStyle}
                  disabled={submitting || cancelandoIds.length > 0}
                >
                  Fechar
                </button>

                {!!itensSolicitaveis.length && (
                  <button
                    type="button"
                    onClick={solicitarRetornoPV}
                    style={buttonPrimaryStyle}
                    disabled={submitting || cancelandoIds.length > 0}
                  >
                    {submitting ? "Solicitando..." : "Solicitar retorno"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuprimentosVendas;
